import { useState, useEffect, useCallback, useRef } from 'react'

// Local debounce utility function with cancel support
const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): T & { cancel: () => void } => {
  let timeout: ReturnType<typeof setTimeout>
  const debounced = ((...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), delay)
  }) as T & { cancel: () => void }

  debounced.cancel = () => {
    clearTimeout(timeout)
  }

  return debounced
}

interface UseScrollPositionProps {
  isManualScroll: boolean
  onScrollChange: (scrollPosition: number) => void
  onManualScrollEnd: () => void
}

export const useScrollPosition = ({
  isManualScroll,
  onScrollChange,
  onManualScrollEnd,
}: UseScrollPositionProps) => {
  const [scrollPosition, setScrollPosition] = useState(0)
  const manualScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleScroll = useCallback(
    debounce(() => {
      const newScrollPosition = window.scrollY
      setScrollPosition(newScrollPosition)
      onScrollChange(newScrollPosition)
      
      if (isManualScroll) {
        if (manualScrollTimeoutRef.current) {
          clearTimeout(manualScrollTimeoutRef.current)
        }
        manualScrollTimeoutRef.current = setTimeout(() => {
          onManualScrollEnd()
          manualScrollTimeoutRef.current = null
        }, 1000) // SCROLL_CONFIG.TIMEOUTS.MANUAL_SCROLL_LONG
      }
    }, 10), // SCROLL_CONFIG.DEBOUNCE.SCROLL
    [isManualScroll, onScrollChange, onManualScrollEnd]
  )

  useEffect(() => {
    window.addEventListener('scroll', handleScroll as EventListener, {
      passive: true,
    })
    
    return () => {
      handleScroll.cancel()
      if (manualScrollTimeoutRef.current) {
        clearTimeout(manualScrollTimeoutRef.current)
      }
      window.removeEventListener('scroll', handleScroll as EventListener)
    }
  }, [handleScroll])

  return scrollPosition
}
